import React, { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Card, CardContent, Grid, Typography, Button, CircularProgress, Divider, Chip, Menu, MenuItem } from "@mui/material";
import { ArrowLeft } from "lucide-react";
import { getRequest, postRequest } from "../../ApiFunction";
import { BASE_URL } from "../../Api";
import { toast } from "react-toastify";

const LoanDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loan, setLoan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [anchorEl, setAnchorEl] = useState(null);

  const handleNotificationMenuClose = () => {
    setAnchorEl(null);
  };

  const handleSendNotification = async (notificationType) => {
    try {
      const result = await postRequest(`${BASE_URL}/loan/send-notification`, {
        loanId: id,
        notificationType
      });
      
      if (result.data.success) {
        toast.success(`${notificationType} sent successfully`);
      } else {
        toast.error(result.data.message || "Failed to send notification");
      }
    } catch (err) {
      toast.error(err.message || "Error sending notification");
    }
    handleNotificationMenuClose();
  };




  const fetchLoanDetails = useCallback(async () => {
    try {
      setLoading(true);
      // Fetch all loans and find the specific one by ID
      const result = await getRequest(`${BASE_URL}/loan/list-employee-loan`);
      const { success, data, message } = result.data;

      if (!success) {
        toast.error(message || "Failed to fetch loan details");
        return;
      }

      // Find the specific loan by ID
      const foundLoan = data?.loans?.find(loan => loan._id === id);
      
      if (foundLoan) {
        setLoan(foundLoan);
      } else {
        toast.error("Loan not found");
      }
    } catch (err) {
      toast.error("Error fetching loan details: " + err.message);
      console.error("Error fetching loan details:", err);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchLoanDetails();
  }, [fetchLoanDetails]);

  if (loading) {
    return (
      <div className="container d-flex justify-content-center align-items-center" style={{ minHeight: "400px" }}>
        <CircularProgress />
      </div>
    );
  }

  if (!loan) {
    return (
      <div className="container">
        <Button startIcon={<ArrowLeft />} onClick={() => navigate("/loan")} sx={{ mb: 3 }}>
          Back to Loans
        </Button>
        <Typography variant="h6">Loan not found</Typography>
      </div>
    );
  }

  const statusColors = {
    "OFFER_SUBMITTED": "warning",
    "APPROVED": "success",
    "DISBURSED": "info",
    "ACTIVE": "success",
    "CLOSED": "default",
    "REJECTED": "error",
    "CANCELLED": "error"
  };

  return (
    <div className="container">
      <Button startIcon={<ArrowLeft />} onClick={() => navigate("/loan")} sx={{ mb: 3 }}>
        Back to Loans
      </Button>

      <Grid container spacing={3}>
        {/* Header Card */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <Typography variant="h5" component="h2">
                  Loan Details
                </Typography>
                <Chip 
                  label={loan.status || "N/A"} 
                  color={statusColors[loan.status] || "default"}
                  sx={{ fontWeight: 600 }}
                />
              </div>
              <Typography color="textSecondary" gutterBottom>
                Application Number: {loan.essApplicationNumber || "N/A"}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Client Information */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom color="primary">
                Client Information
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="textSecondary">Full Name</Typography>
                  <Typography variant="body1">{loan.clientData?.fullName || "N/A"}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="textSecondary">NIN</Typography>
                  <Typography variant="body1">{loan.clientData?.nin || "N/A"}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="textSecondary">Mobile Number</Typography>
                  <Typography variant="body1">{loan.clientData?.mobileNumber || "N/A"}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="textSecondary">Email</Typography>
                  <Typography variant="body1">{loan.clientData?.emailAddress || "N/A"}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="textSecondary">Gender</Typography>
                  <Typography variant="body1">{loan.clientData?.sex === "M" ? "Male" : loan.clientData?.sex === "F" ? "Female" : "N/A"}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="textSecondary">Bank Account</Typography>
                  <Typography variant="body1">{loan.clientData?.bankAccountNumber || "N/A"}</Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Loan Information */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom color="primary">
                Loan Information
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="textSecondary">Requested Amount</Typography>
                  <Typography variant="body1" fontWeight={600}>
                    TZS {loan.requestedAmount?.toLocaleString() || "0"}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="textSecondary">Product Code</Typography>
                  <Typography variant="body1">{loan.productCode || loan.loanData?.productCode || "N/A"}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="textSecondary">Tenure</Typography>
                  <Typography variant="body1">{loan.tenure || loan.loanData?.tenure || "N/A"} Months</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="textSecondary">Annual Interest Rate</Typography>
                  <Typography variant="body1">
                    {loan.annualInterestRate != null ? `${loan.annualInterestRate}%` : "N/A"}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="textSecondary">Total Interest Amount</Typography>
                  <Typography variant="body1">
                    TZS {loan.loanData?.interestRate != null ? Number(loan.loanData.interestRate).toLocaleString() : "N/A"}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="textSecondary">Processing Fee</Typography>
                  <Typography variant="body1">TZS {loan.loanData?.processingFee ? Number(loan.loanData.processingFee).toLocaleString() : "0"}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="textSecondary">Insurance</Typography>
                  <Typography variant="body1">TZS {loan.loanData?.insurance ? Number(loan.loanData.insurance).toLocaleString() : "0"}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="textSecondary">Loan Purpose</Typography>
                  <Typography variant="body1">{loan.loanData?.loanPurpose || "N/A"}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="textSecondary">Funding</Typography>
                  <Typography variant="body1">{loan.loanData?.funding || "N/A"}</Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Employment Information */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom color="primary">
                Employment Information
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="textSecondary">Basic Salary</Typography>
                  <Typography variant="body1">TZS {loan.employmentData?.basicSalary?.toLocaleString() || "N/A"}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="textSecondary">Net Salary</Typography>
                  <Typography variant="body1">TZS {loan.employmentData?.netSalary?.toLocaleString() || "N/A"}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="textSecondary">Employment Date</Typography>
                  <Typography variant="body1">{loan.clientData?.employmentDate || "N/A"}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="textSecondary">Designation</Typography>
                  <Typography variant="body1">{loan.employmentData?.designationName || "N/A"}</Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Timestamps */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom color="primary">
                Timeline
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="textSecondary">Created At</Typography>
                  <Typography variant="body1">
                    {loan.createdAt ? new Date(loan.createdAt).toLocaleString() : "N/A"}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="textSecondary">Updated At</Typography>
                  <Typography variant="body1">
                    {loan.updatedAt ? new Date(loan.updatedAt).toLocaleString() : "N/A"}
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleNotificationMenuClose}
      >
        <MenuItem onClick={() => handleSendNotification('LOAN_DISBURSEMENT_NOTIFICATION')}>
          Send Disbursement Notification
        </MenuItem>
        <MenuItem onClick={() => handleSendNotification('LOAN_DISBURSEMENT_FAILURE_NOTIFICATION')}>
          Send Disbursement Failure Notification
        </MenuItem>
      </Menu>
    </div>
  );
};

export default LoanDetail;
